import { Component, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { BrandAccessRight } from 'helpers';
import { RetailerPrivilege } from 'helpers/team-user-roles';
import { AccessState } from 'models';

import { extractBrandIdFromUrl } from '../../../b2b-order/retailer-platform/order/shop/no-access-product/no-access-product.component';
import { BrandAccessService } from '../../../retailer-platform/brands/providers/brand-access.service';
import { UserService } from '../user.service';

// reuse-no-access: call this guard in the route definition, with the property `product` (see example in order-routing.module.ts)
export function productAccessGuard(product: BrandAccessRight, additionalPrivileges: RetailerPrivilege[] = []) {
    return (component: Component, currentRoute?): Observable<boolean | UrlTree> => {
        const brandId = extractBrandIdFromUrl(currentRoute.url);
        const userService = inject(UserService);
        const router = inject(Router);
        const brandAccessService = inject(BrandAccessService);

        const requiredPrivileges = [RetailerPrivilege.Admin, RetailerPrivilege.Buyer, ...additionalPrivileges];

        if (!userService.hasAtLeastOnePrivilege(requiredPrivileges)) {
            return of(false);
        }

        if (!brandId) {
            return of(true);
        }

        return brandAccessService.pullBrandById(brandId).pipe(
            first(),
            map((brand) => {
                const productAccessRight = brand?.relations[product];
                if (productAccessRight !== AccessState.Granted) {
                    return createNoAccessUrl(currentRoute.url, router);
                }
                return true;
            })
        );
    };
}

function createNoAccessUrl(url: string, router: Router): UrlTree {
    const segments: Array<string> = url.split('/');
    const lastSegment = segments.pop();

    if (lastSegment?.includes('search')) {
        return router.parseUrl(`${segments.join('/')}/no-access`);
    } else {
        return router.parseUrl(`${url}/no-access`);
    }
}
